import React, { ChangeEventHandler, useId, useMemo, useState } from 'react';

import ChevronIcon from '@/assets/chevron.svg?react';
import OptionItem from '@/components/ui/ComboBox/OptionItem';
import VirtualOptionList from '@/components/ui/ComboBox/VirtualOptionList';
import { selectClasses } from '@/components/ui/Select/Select';
import { RESPONSIVE_QUERY } from '@/consts';
import { EnumLike } from '@/types';
import cn from '@/utils/cn';

import { ScrollArea } from '../Select/ScrollArea';
import { sortArrayByQuery } from './utils/sortArrayByQuery';
import { Combobox as PrimitiveComboBox } from '@headlessui/react';
import { Float } from '@headlessui-float/react';
import { FieldValues } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';

interface Props extends React.ComponentPropsWithRef<'input'> {
  field: FieldValues;
  options: EnumLike[];
  placeholder: string;
  success?: boolean;
  error?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onBlur?: () => void;
  onFocus?: () => void;
  withSearchOnMobile?: boolean;
  scrollAreaMaxHeightClassName?: string;
  /**
   * Renderuje tylko widoczne opcje - dla długich list (kilkaset pozycji),
   * gdzie montowanie wszystkich opcji naraz zamula przeglądarkę.
   */
  virtualized?: boolean;
  /**
   * Nie pokazuje listy, dopóki zapytanie nie osiągnie tej długości - dla list,
   * w których wspólny fragment etykiety pasuje do wszystkich pozycji.
   * 0 wyłącza próg.
   */
  minQueryLength?: number;
}

const ComboBox: React.FC<Props> = ({
  field,
  options,
  placeholder,
  success,
  error,
  className,
  onChange,
  onBlur,
  onFocus,
  withSearchOnMobile,
  disabled,
  scrollAreaMaxHeightClassName,
  virtualized,
  minQueryLength = 0,
}) => {
  const id = useId();
  const isDesktop = useMediaQuery({
    query: RESPONSIVE_QUERY.IS_DESKTOP,
  });

  const ref = React.useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState('');

  const filteredItems = useMemo(
    () =>
      query === ''
        ? options
        : sortArrayByQuery(
            options.filter((item) => {
              return item.label.toLowerCase().includes(query.toLowerCase());
            }),
            query
          ),
    [options, query]
  );

  const isQueryTooShort = query.length < minQueryLength;

  const visibleItems = isQueryTooShort ? [] : filteredItems;

  const scrollAreaMaxHeight = cn(
    'max-h-[200px] md:max-h-[266px]',
    scrollAreaMaxHeightClassName
  );

  return (
    <>
      {(isDesktop || withSearchOnMobile) && (
        <div
          className={cn(
            'relative w-full flex-col gap-1 lg:flex',
            !withSearchOnMobile && 'hidden',
            className
          )}
        >
          <PrimitiveComboBox
            ref={ref}
            disabled={disabled}
            as="div"
            className={cn(
              'relative w-full md:block',
              !withSearchOnMobile && 'hidden',
              className
            )}
            onBlur={() => {
              if (onBlur) {
                filteredItems.length === 1 &&
                  field.onBlur(filteredItems[0].value);
                onBlur();
                return;
              }
              filteredItems.length === 1 &&
                field.onChange(filteredItems[0].value);
            }}
            onChange={(value) => {
              if (onChange) {
                const selectedValue = options.find(
                  (item) => item.label === value
                )?.value;
                if (selectedValue !== undefined) {
                  const syntheticEvent = {
                    target: { value: selectedValue },
                  } as React.ChangeEvent<HTMLInputElement>;
                  onChange(syntheticEvent);
                }
                return;
              }
              field.onChange(
                options.find((item) => item.label === value)?.value
              );
            }}
            onFocus={onFocus}
            value={
              options.find((item) => item.value === field.value)?.label || ''
            }
          >
            {({ open }) => (
              <>
                <Float
                  as="div"
                  placement="bottom"
                  enter="transition-all duration-100 ease-in-out"
                  enterFrom="opacity-0 -translate-y-3"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition-none duration-0 ease-in-out"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  offset={4}
                  onHide={() => {
                    setQuery('');
                  }}
                  portal
                  adaptiveWidth
                >
                  <PrimitiveComboBox.Button className="group w-full" as="div">
                    <PrimitiveComboBox.Input
                      placeholder={placeholder}
                      id={id}
                      className={cn(
                        selectClasses(),
                        'cursor-pointer pr-[34px] placeholder:text-dark-gray-200',
                        'body text-oxford-blue-500',
                        '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
                        success &&
                          'border-green-400 hover:border-green-400 focus:border-green-400',
                        open
                          ? 'border-blue-600 hover:border-blue-600 focus:border-blue-600 focus:ring-0'
                          : 'focus:ring-4',
                        'group-hover:border-blue-600',
                        success &&
                          'border-green-400 group-hover:border-green-400 hover:border-green-400 focus:border-green-400',
                        disabled &&
                          'border-dark-gray-200 group-hover:border-dark-gray-200 hover:border-dark-gray-200',
                        !!error && 'border-red-400'
                      )}
                      autoComplete="off"
                      onChange={(event) => setQuery(event.target.value)}
                      data-testid={`input-${field.name}`}
                    />

                    <div className="absolute right-4 top-1/2 flex -translate-y-1/2 items-center gap-2.5">
                      <div
                        className={cn(
                          'flex items-center bg-white pl-1.5',
                          disabled && 'cursor-not-allowed bg-gray-200'
                        )}
                      >
                        <ChevronIcon
                          width={14}
                          height={14}
                          className={cn(
                            'shrink-0 rotate-90 cursor-pointer text-dark-gray-300 transition-transform duration-300 ease-in-out',
                            open && 'rotate-[270deg]',
                            disabled && 'cursor-not-allowed'
                          )}
                        />
                      </div>
                    </div>
                  </PrimitiveComboBox.Button>

                  <PrimitiveComboBox.Options
                    className={cn(
                      'mt-[2px] bg-white p-1.5',
                      'w-[var(--radix-select-trigger-width)]',
                      'data-[state=open]:duration-150 data-[state=closed]:ease-in-out data-[state=open]:ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[side=bottom]:slide-in-from-top-2',
                      'rounded-lg border border-gray-200 shadow-shadow14'
                    )}
                  >
                    {virtualized && visibleItems.length > 0 ? (
                      <VirtualOptionList
                        options={visibleItems}
                        fieldName={field.name}
                        selectedValue={field.value}
                        mah={scrollAreaMaxHeight}
                        withScrollbarSpace={visibleItems.length > 7}
                      />
                    ) : (
                      <ScrollArea mah={scrollAreaMaxHeight}>
                        <div
                          className={cn(
                            'flex flex-col gap-1 border-gray-400',
                            visibleItems.length > 7 && 'pr-3.5'
                          )}
                        >
                          {/* in order to not set first value */}
                          <OptionItem
                            item={{ value: '', label: '' }}
                            key={'empty'}
                            className={cn('hidden')}
                            data-testid={`option-${field.name}-empty`}
                          />
                          {visibleItems.map((item) => (
                            <OptionItem
                              item={item}
                              key={item.value}
                              data-testid={`option-${field.name}-${item.value}`}
                              className={cn(
                                item.value === field.value && 'font-semibold'
                              )}
                            />
                          ))}
                          {isQueryTooShort && (
                            <OptionItem
                              item={{
                                label: `Wprowadź co najmniej ${minQueryLength} ${
                                  minQueryLength >= 5 ? 'znaków' : 'znaki'
                                }`,
                                value: 'none',
                              }}
                              key={'No query found'}
                              data-testid={`option-${field.name}-no-query`}
                              className={cn(
                                'pointer-events-none cursor-not-allowed hover:bg-white'
                              )}
                            />
                          )}
                          {!isQueryTooShort &&
                            query &&
                            filteredItems.length === 0 && (
                              <OptionItem
                                item={{ label: 'Brak wyników', value: 'none' }}
                                key={'No results found'}
                                data-testid={`option-${field.name}-no-results`}
                                className={cn(
                                  'pointer-events-none cursor-not-allowed hover:bg-white'
                                )}
                              />
                            )}
                        </div>
                      </ScrollArea>
                    )}
                  </PrimitiveComboBox.Options>
                </Float>
              </>
            )}
          </PrimitiveComboBox>
        </div>
      )}
      {!isDesktop && !withSearchOnMobile && (
        <div className="relative">
          <div className="absolute right-[41px] top-1/2 z-30 flex -translate-y-1/2 items-center gap-2.5"></div>
          <div className="pointer-events-none absolute right-4 top-1/2 z-30 flex -translate-y-1/2 items-center gap-2.5">
            <ChevronIcon
              width={14}
              height={14}
              color={'#8890A3'}
              className={cn(
                'shrink-0 rotate-90 fill-[currentColor] transition-transform duration-300 ease-in-out'
              )}
            />
          </div>
          <select
            id={id}
            name={id}
            disabled={disabled}
            value={field.value ?? ''}
            className={cn(
              selectClasses({ screen: 'mobile' }),
              'pr-[50px]',
              !!error && 'border-red-400',
              success &&
                'border-green-400 hover:border-green-400 focus:border-green-400',
              disabled &&
                'border-dark-gray-200 group-hover:border-dark-gray-200 hover:border-dark-gray-200',
              field.value ? 'text-oxford-blue-400' : 'text-dark-gray-200'
            )}
            onChange={(e) => {
              if (onChange) {
                const syntheticEvent = {
                  target: { value: e.target.value },
                } as React.ChangeEvent<HTMLInputElement>;
                onChange(syntheticEvent);
                return;
              }
              field.onChange(e.target.value);
            }}
          >
            <option
              value=""
              disabled
              hidden
              data-testid={`option-${field.name}-empty`}
            >
              {placeholder}
            </option>
            {options.map((option) => (
              <option
                key={id + option.value + option.label}
                value={option.value}
                data-testid={`option-${field.name}-${option.value}`}
              >
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </>
  );
};

export default ComboBox;
